import { Box, Stack, Tab } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { formatDateWithTime } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import {
  WalletTransactionListColumnSortable,
  WalletTransactionListRequest,
} from "../../../services/Endpoints/wallet/WalletTransactionListEndpoint";
import { FilterOperatorEnum, SortDirectionEnum } from "../../../services/Schemas/ListQueryString";
import TextBadge from "../../common/info-badge/TextBadge";
import {
  changeToToman,
  formatCurrency,
} from "../../../utils/currency-formater";
import { routerPaths } from "../../../constants/Paths";
import { TrendDown, TrendUp } from "iconsax-react";

import { useUrl } from "../../../hooks/urlEncoding";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import useUrlSort from "../../../hooks/useUrlSort";
import UserLink from "../common/UserLink";
import { AccessEnums } from "../../../constants/accessEnum";
import { useCanAccess } from "../../../hooks/canAccess";
import { BroadcastingListColumnFilterable, BroadcastingListColumnSortable } from "../../../services/Endpoints/broadcast/BroadcastingListEndpoint";
import { WalletListColumnFilterable, WalletListColumnSortable } from "../../../services/Endpoints/wallet/WalletListEndpoint";

interface ITransaction {
  credit: string | ReactNode;
  debit: string | ReactNode;
  description: string;
  wallet_id: any;
  created_at: string;
  voice_broadcast_id: any;
  id: number;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

export default function WalletTransactionTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { parseQueryString, setParamWithSearchParams, queryStringUrl } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const { lang, dir } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<ITransaction[] | []>(
    []
  );
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const [total, setTotal] = useState(0);
  const location = useLocation();
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new WalletTransactionListRequest(
        protectedInstance
      );
      const resp = await walletListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.wallet_transactions.map(
        ({
          created_at,
          credit,
          debit,
          description,
          wallet_id,
          id,
          voice_broadcast_id,
        }) => ({
          created_at,
          wallet_id: wallet_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminWalletsList)}
              user={{ id: wallet_id }}
              link={queryStringUrl(routerPaths.WALLET, {
                filters: {
                  [WalletListColumnFilterable.ID]: {
                    column: WalletListColumnFilterable.ID,
                    value: wallet_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [WalletListColumnSortable.ID]: {
                    column: WalletListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          voice_broadcast_id: voice_broadcast_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
              user={{ id: voice_broadcast_id }}
              link={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                filters: {
                  [BroadcastingListColumnFilterable.ID]: {
                    column: BroadcastingListColumnFilterable.ID,
                    value: voice_broadcast_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [BroadcastingListColumnSortable.ID]: {
                    column: BroadcastingListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          id,
          credit: (
            <TextBadge
              sxStyle={{
                backgroundColor: ({ palette }: any) => palette.others.doneBg,
                color: ({ palette }: any) => palette.others.done,
                // borderColor: ({ palette }: any) => palette.others.done,
                // borderWidth: "1px",
                // borderStyle: "solid",
              }}
              text={
                <Stack
                  direction="row"
                  gap="4px"
                  justifyContent="center"
                  alignItems="center"
                >
                  <TrendUp size="18" variant="Outline" />
                  {formatCurrency(changeToToman(`${credit}`))}
                </Stack>
              }
            />
          ),
          description,
          debit: (
            <TextBadge
              sxStyle={{
                backgroundColor: ({ palette }: any) =>
                  palette.others.deadlineBg,
                color: ({ palette }: any) => palette.others.deadline,
              }}
              text={
                <Stack direction="row" gap="4px" justifyContent="center">
                  <TrendDown size="18" variant="Outline" />
                  {formatCurrency(changeToToman(`${debit}`))}
                </Stack>
              }
            />
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const transactionColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "wallet_id",
      label: "شناسه کیف پول",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "voice_broadcast_id",
      label: "شناسه کمپین",
      minWidth: 150,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "created_at",
      label: "تاریخ",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) =>
        value && formatDateWithTime({ lang, date: value }),
    },
    {
      key: "credit",
      label: "واریز ها",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "debit",
      label: "برداشت ها",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "description",
      label: "توضیحات",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
  ];
  const { filtersSchema, columns } = useTableSchema(transactionColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
